import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isSameDay } from 'date-fns'
import {
  getDateTimeWithoutTimezone,
  nowOnClinicTimezone,
  nowOnTimezone,
} from '@/v2/utils/convert'
import TimelineCard from './TimelineCard'
import {
  pxPerMinute,
  hourMarks,
  halfHourMarks,
  minutesToY,
  positionEvents,
  rangeToRect,
  snapToStep,
  yToMinutes,
  minCardPx,
  padTop,
  PAD_BOTTOM,
  isAppointmentDraggable,
  droppableColumns,
  formatHourLabel,
} from './timelineUtils'
import {
  TimelineScroll,
  Content,
  Gutter,
  HourLabel,
  Lane,
  Column,
  HeaderRow,
  HeaderSpacer,
  ColumnHeader,
  HourLine,
  OffHoursBlock,
  NowLine,
  DropTarget,
  Scrim,
} from './styles'

// Daily timeline (desktop + mobile). Renders every chair (room/doctor column)
// side-by-side, fit to the available width like the old grid, sharing a single
// time gutter and one vertical scroller. Each chair keeps the drag-to-reschedule
// cards, and a drag moves in both axes: up/down changes the time, left/right
// moves the appointment to another chair. `isMobile` only tunes sizing
// (gutter/header) — the drag works identically on pointer and touch.
const Timeline = ({
  columns,
  events,
  offHours,
  startMin,
  endMin,
  date,
  isMobile = false,
  showDoctorName = true,
  onOpenEvent,
  onMoveRequest,
  onCreate,
}) => {
  const scrollRef = useRef(null)
  // The chairs' shared box: a card being dragged sideways hit-tests the pointer
  // against this to work out which chair it's over.
  const laneRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  // Which chair the card in hand is currently over, so it can be outlined.
  const [dropColumn, setDropColumn] = useState(null)

  // Stable identity: the cards depend on this in an effect, and a fresh function
  // each render would re-fire it constantly mid-drag.
  const handleDragActiveChange = useCallback((active, targetColumn) => {
    setIsDragging(active)
    setDropColumn(active ? targetColumn : null)
  }, [])

  // Desktop reads tighter (80px/hour, like the old grid); mobile stays roomy.
  const pxPerMin = pxPerMinute(isMobile)
  const minCard = minCardPx(isMobile)
  const topPad = padTop(isMobile)

  const totalHeight = (endMin - startMin) * pxPerMin + topPad + PAD_BOTTOM

  const isToday = useMemo(
    () =>
      isSameDay(
        getDateTimeWithoutTimezone(`${date}T00:00`),
        nowOnClinicTimezone()
      ),
    [date]
  )
  const nowMin = isToday ? nowOnTimezone('minutes') : null
  const showNow = isToday && nowMin >= startMin && nowMin <= endMin

  const marks = useMemo(() => hourMarks(startMin, endMin), [startMin, endMin])
  // Desktop draws a solid rule on each half hour too, matching the old grid's
  // 30-minute slot lines; mobile keeps just the hour rules. The gutter is only
  // labelled on the hour, though.
  const halfMarks = useMemo(
    () => (isMobile ? [] : halfHourMarks(startMin, endMin)),
    [isMobile, startMin, endMin]
  )

  // useDayData tags every event with a resourceId but doesn't split by column, so
  // group the events per chair and lay out overlaps within each chair's width.
  const positionedByColumn = useMemo(
    () =>
      columns.map(col =>
        positionEvents(
          events.filter(ev => ev.resourceId === col.id),
          pxPerMin,
          minCard
        )
      ),
    [columns, events, pxPerMin, minCard]
  )

  // Per-column drop rules and header names, memoised so a card's drag listeners
  // aren't torn down and re-attached on every render.
  const droppableByColumn = useMemo(
    () => columns.map((_, ci) => droppableColumns(columns, ci)),
    [columns]
  )
  const columnTitles = useMemo(() => columns.map(col => col.title), [columns])

  // Scroll the work day into view on mount: to "now" when today, else the top.
  useEffect(() => {
    const node = scrollRef.current
    if (!node) return
    const focusMin = showNow ? nowMin - 60 : startMin
    node.scrollTop = Math.max(
      0,
      minutesToY(focusMin, startMin, pxPerMin) + topPad
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Tap on empty time in a chair's column creates an appointment there.
  const handleColumnClick = (e, columnId) => {
    // Ignore taps that land on a card (those open/drag the appointment).
    if (e.target.closest('[data-card]')) return
    const rect = e.currentTarget.getBoundingClientRect()
    const minutes = snapToStep(
      yToMinutes(e.clientY - rect.top, startMin, pxPerMin)
    )
    onCreate(Math.min(Math.max(minutes, startMin), endMin), columnId)
  }

  return (
    <TimelineScroll ref={scrollRef}>
      <HeaderRow $isMobile={isMobile}>
        <HeaderSpacer $isMobile={isMobile} />
        {columns.map((col, ci) => (
          <ColumnHeader
            key={col.id}
            $index={ci}
            $isMobile={isMobile}
            title={col.title}
          >
            {col.title}
          </ColumnHeader>
        ))}
      </HeaderRow>
      <Content $height={totalHeight}>
        <Gutter $isMobile={isMobile}>
          {marks.map(m => (
            <HourLabel
              key={m}
              $top={minutesToY(m, startMin, pxPerMin)}
              $isMobile={isMobile}
            >
              {formatHourLabel(m, isMobile)}
            </HourLabel>
          ))}
        </Gutter>
        <Lane ref={laneRef} $isMobile={isMobile}>
          {/* Hour rules and the now-line span every chair, drawn once. */}
          {marks.map(m => (
            <HourLine
              key={m}
              $top={minutesToY(m, startMin, pxPerMin)}
              $isMobile={isMobile}
            />
          ))}
          {halfMarks.map(m => (
            <HourLine
              key={`half-${m}`}
              $top={minutesToY(m, startMin, pxPerMin)}
              $isMobile={isMobile}
            />
          ))}
          {showNow && (
            <NowLine
              $top={minutesToY(nowMin, startMin, pxPerMin)}
              $isMobile={isMobile}
            />
          )}
          {/* Only worth drawing when there's more than one chair to choose
              between; with a single column it would just frame the whole lane. */}
          {dropColumn != null && columns.length > 1 && (
            <DropTarget $index={dropColumn} $count={columns.length} />
          )}
          {columns.map((col, ci) => (
            <Column
              key={col.id}
              $index={ci}
              $count={columns.length}
              $isMobile={isMobile}
              onClick={e => handleColumnClick(e, col.id)}
            >
              {(offHours[ci] || []).map((oh, i) => {
                const rect = rangeToRect(
                  oh.startsAt,
                  oh.endsAt,
                  startMin,
                  endMin,
                  pxPerMin
                )
                return rect ? (
                  <OffHoursBlock
                    key={i}
                    $top={rect.top}
                    $height={rect.height}
                    $isMobile={isMobile}
                  />
                ) : null
              })}
              {positionedByColumn[ci].map(item => {
                const rect = rangeToRect(
                  item.startMin,
                  item.endMin,
                  startMin,
                  endMin,
                  pxPerMin,
                  minCard
                )
                if (!rect) return null
                return (
                  <TimelineCard
                    key={`${item.ev.event.type}-${item.ev.event.id}`}
                    calEvent={item.ev}
                    isMobile={isMobile}
                    showDoctorName={showDoctorName}
                    draggable={isAppointmentDraggable(item.ev.event)}
                    scrollRef={scrollRef}
                    laneRef={laneRef}
                    columnIndex={ci}
                    columnCount={columns.length}
                    droppableColumns={droppableByColumn[ci]}
                    columnTitles={columnTitles}
                    startMin={startMin}
                    endMin={endMin}
                    pxPerMin={pxPerMin}
                    top={rect.top}
                    height={rect.height}
                    leftPct={(item.lane / item.laneCount) * 100}
                    widthPct={(1 / item.laneCount) * 100}
                    baseStartMin={item.startMin}
                    durationMin={item.endMin - item.startMin}
                    onOpen={() => onOpenEvent(item.ev)}
                    onMove={(newStartMin, newColumnIndex) =>
                      onMoveRequest(
                        item.ev,
                        newStartMin,
                        (columns[newColumnIndex] || col).id
                      )
                    }
                    onDragActiveChange={handleDragActiveChange}
                  />
                )
              })}
            </Column>
          ))}
        </Lane>
        {isDragging && <Scrim />}
      </Content>
    </TimelineScroll>
  )
}

export default Timeline
